/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { CompletionStatus, FetchType } from '../../../../enums';
import { LearnerCareerPathSkillSet } from '../../../../models/view/learner-career-path-skill-set';
import { useAppActions, useAppState } from '../../../../overmind';
import classNames from 'classnames';
import { PagePath } from '../../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { Anchor, Button, CheckIcon, Chip, ChipWrapper, GridItemLayout, GridLayout, PageHeader, useMatchScreenWidth } from '@keplerco/core';
import { getSubdomain } from '../../../../library/helpers/get-subdomain';
import layoutStyles from '../../../../design/layout.module.css';
import styles from './rating-career-paths.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { useAnalysisBasePagePath } from '../../use-analysis-base-page-path';
import KeplerNavlink from '../../../../navigation/guards/kepler-navlink';
import { generateLPIText } from '../../career-path/career-path.helpers';

export function RateYourSkillsPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();

  const actions = useAppActions();
  const { forcedAssessments, activeAssessmentConfig } = useAppState();

  const [careerPaths, setPaths] = useState<LearnerCareerPathSkillSet[]>();

  const isMobile = useMatchScreenWidth('mobile');

  useEffect(() => {
    actions.startLoader({ path: PagePath.analysisRateYourSkills, type: FetchType.PageFetching });

    actions.getLearnerCareerPathSkillSets().then(paths => {
      setPaths(paths);
      actions.stopLoader(PagePath.analysisRateYourSkills);
    });
  }, []);

  const allAreComplete = careerPaths?.every(u => u.completionStatus === CompletionStatus.Completed);
  const subdomain = getSubdomain();

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.analysisRateYourSkills}>
        <div className="wrapper pageWrapper" style={{ gap: 24 }}>
          <PageHeader
            breadcrumbs={
              forcedAssessments.length > 1
                ? [
                    {
                      name: 'Pending assessments',
                      url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
                    },
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: `Choose your ${generateLPIText(subdomain)}s`,
                      url: basePagePath + PagePath.analysisChooseYourCareerPaths,
                    },
                    {
                      name: subdomain.includes('lpi') ? 'Capability map' : 'Rate your skills',
                      url: location.pathname,
                    },
                  ]
                : [
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: `Choose your ${generateLPIText(subdomain)}s`,
                      url: basePagePath + PagePath.analysisChooseYourCareerPaths,
                    },
                    {
                      name: subdomain.includes('lpi') ? 'Capability map' : 'Rate your skills',
                      url: location.pathname,
                    },
                  ]
            }
            title={subdomain.includes('lpi') ? 'Capability map' : 'Rate your skills'}
            subtitle={`This assessment will determine which skills you need to focus on${subdomain.includes('lpi') ? '' : ', and will populate your custom learning journey'}`}
          />

          {!!careerPaths && (
            <div
              style={{
                marginTop: 24,
                overflowY: 'auto',
                paddingBottom: 120,
              }}
            >
              <GridLayout columnCount={3}>
                {careerPaths.map(path => {
                  const done = path.completionStatus === CompletionStatus.Completed;

                  return (
                    <GridItemLayout key={path.skillSetSlug}>
                      <KeplerNavlink to={basePagePath + PagePath.analysisRateYourSkillsRating.replace(':ratingSlug', path.skillSetSlug)} style={{ textDecoration: 'none' }}>
                        <div className={classNames('card', styles.card, { [styles.done]: done })}>
                          {done && (
                            <div className={styles.checkIconBackground}>
                              <CheckIcon tone="contrast-text" />
                            </div>
                          )}

                          <ChipWrapper>
                            <Chip customTheme={{ backgroundColor: 'background', borderColor: 'baby-blue', color: 'text' }}>{path.skillCount} skill(s)</Chip>
                            {done && <Chip themeColour="g">Done</Chip>}
                          </ChipWrapper>

                          <h3 style={{ paddingTop: 5, paddingBottom: 5 }}>{path.skillSetName}</h3>

                          <p>
                            {path.skillSetDescription ?? (
                              <React.Fragment>
                                No skill description was found in the api for <strong>{path.skillSetName}</strong>. Don't worry, you can still rate your abilities in this skill set
                              </React.Fragment>
                            )}
                          </p>

                          {!done && (
                            <div style={{ marginTop: 'auto', marginLeft: 'auto', paddingTop: 15 }}>
                              <Anchor>Rate your skills</Anchor>
                            </div>
                          )}
                        </div>
                      </KeplerNavlink>
                    </GridItemLayout>
                  );
                })}
              </GridLayout>
            </div>
          )}
        </div>
      </PageLoaderLayer>

      <footer className={layoutStyles.footer}>
        <div className={layoutStyles.footerContent}>
          <Anchor arrow reverse block onClick={() => keplerNavigate(basePagePath + PagePath.analysisChooseYourCareerPaths)}>
            Back
          </Anchor>

          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            {!allAreComplete && !isMobile && <span style={{ color: 'var(--accent-3)' }}>To continue, rate your skills</span>}

            <Button
              type="button"
              disabled={!allAreComplete}
              onClick={async () => {
                actions.startLoader({ path: PagePath.analysisRateYourSkills, type: FetchType.Sending });
                await actions.getForcedAssessments();
                actions.clearAssessmentConfig();
                actions.stopLoader(PagePath.analysisRateYourSkills);
              }}
            >
              Continue
            </Button>
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
}
